import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { Input } from '@/components/form/Input';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { type CredentialIdentity } from '@/sections/asset/components/tabs/credentials/credentialForms/types';
import { type IdentityError } from '@/sections/asset/components/tabs/credentials/credentialForms/validators';

type IdentityTab = 'basics' | 'credentials' | 'advanced';

interface IdentityEditorProps {
  identity: CredentialIdentity;
  onChange: (next: CredentialIdentity) => void;
  attempted: boolean;
  error?: IdentityError | null;
  // Controlled active tab for THIS identity — owned by IdentitySetManager
  // (keyed per identity id) so switching the open identity restores its tab.
  activeTab: IdentityTab;
  onActiveTabChange: (tab: IdentityTab) => void;
  // Opens the (single, SetManager-owned) MFA dialog for THIS identity.
  onOpenMfa: () => void;
}

// IdentityEditor is the expanded content of one accordion identity. It edits ONE
// identity across three shadcn boxed tabs:
//   - Basics: the editable identity label + the Standard/Elevated privilege toggle.
//   - Credentials: the per-identity Login URL + the login-inputs key/value editor
//     (locked username/password rows + optional extra rows) + the recorder note.
//   - Advanced: a summary row + an "Add/Edit MFA" launcher that opens a separate
//     optional MFA dialog (2FA/TOTP is NOT edited inline here — it lives behind
//     the launcher via onOpenMfa).
// Tabs render an error dot after an attempted submit on whichever tab holds an
// unmet requirement (Basics = label; Credentials = loginUrl/inputs), read from
// the existing IdentityError. The accordion header (owned by IdentitySetManager)
// shows the label read-only and owns the remove control — typing here updates it
// live via onChange. No useMemo/useCallback: React Compiler handles memoization.
export function IdentityEditor({
  identity,
  onChange,
  attempted,
  error,
  activeTab,
  onActiveTabChange,
  onOpenMfa,
}: IdentityEditorProps) {
  const updateInput = (index: number, field: 'key' | 'value', val: string) => {
    const next = [...identity.inputs];
    next[index] = { ...next[index], [field]: val };
    onChange({ ...identity, inputs: next });
  };

  const showError = attempted && error;
  const basicsTabHasError = !!error?.label;
  const credentialsTabHasError = !!(error?.loginUrl || error?.inputs);

  return (
    <Tabs
      value={activeTab}
      onValueChange={value => onActiveTabChange(value as IdentityTab)}
      className="flex flex-col gap-4"
    >
      <TabsList variant="boxed">
        <TabsTrigger
          variant="boxed"
          value="basics"
          data-testid="identity-tab-basics"
        >
          Basics
          {showError && basicsTabHasError && (
            <span
              aria-hidden
              className="bg-chariot-stroke-critical ml-1.5 size-1.5 rounded-full"
              data-testid="identity-tab-dot-basics"
            />
          )}
        </TabsTrigger>
        <TabsTrigger
          variant="boxed"
          value="credentials"
          data-testid="identity-tab-credentials"
        >
          Credentials
          {showError && credentialsTabHasError && (
            <span
              aria-hidden
              className="bg-chariot-stroke-critical ml-1.5 size-1.5 rounded-full"
              data-testid="identity-tab-dot-credentials"
            />
          )}
        </TabsTrigger>
        <TabsTrigger
          variant="boxed"
          value="advanced"
          data-testid="identity-tab-advanced"
        >
          Advanced
        </TabsTrigger>
      </TabsList>

      {/* Basics: identity label + privilege toggle. */}
      <TabsContent value="basics" className="flex flex-col gap-4">
        {/* Identity label — names this identity; the accordion header reflects it
            (read-only) live via onChange. */}
        <Input
          label="Identity label"
          name="identity-label"
          value={identity.label}
          onChange={e => onChange({ ...identity, label: e.target.value })}
          placeholder="e.g. admin-user"
          required
          error={showError ? error?.label : undefined}
          data-testid="identity-label-input"
        />

        {/* Privilege toggle (Standard / Elevated) */}
        <div>
          <span className="text-chariot-text-primary mb-2 block text-sm font-medium">
            Privilege
          </span>
          <div className="flex gap-2" role="group" aria-label="Privilege level">
            <Button
              variant="ghost"
              size="sm"
              aria-pressed={identity.privilege === 'standard'}
              className={
                identity.privilege === 'standard'
                  ? 'bg-chariot-background-secondary'
                  : undefined
              }
              onClick={() => onChange({ ...identity, privilege: 'standard' })}
              data-testid="identity-privilege-standard"
            >
              Standard
            </Button>
            <Button
              variant="ghost"
              size="sm"
              aria-pressed={identity.privilege === 'elevated'}
              className={
                identity.privilege === 'elevated'
                  ? 'bg-chariot-background-secondary'
                  : undefined
              }
              onClick={() => onChange({ ...identity, privilege: 'elevated' })}
              data-testid="identity-privilege-elevated"
            >
              Elevated
            </Button>
          </div>
        </div>
      </TabsContent>

      {/* Credentials: per-identity Login URL + login-inputs editor + recorder note. */}
      <TabsContent value="credentials" className="flex flex-col gap-4">
        {/* Per-identity Login URL */}
        <Input
          label="Login URL"
          name="identity-login-url"
          value={identity.loginUrl}
          onChange={e => onChange({ ...identity, loginUrl: e.target.value })}
          placeholder="https://app.example.com/login"
          required
          error={showError ? error?.loginUrl : undefined}
          data-testid="identity-login-url-input"
        />

        {/* Login inputs key/value editor (markup lifted from ComplexLoginForm's
            LoginInputsField). The first two rows are the REQUIRED username +
            password (their keys are fixed and read-only — validateIdentity requires
            the literal `username`/`password` keys). Rows beyond those two are
            optional extra key/value pairs. */}
        <div>
          <label className="text-chariot-text-primary mb-2 block text-sm font-medium">
            Login Inputs
          </label>
          <p className="text-chariot-text-secondary mb-3 text-sm">
            {`Username and password are required. Add optional extra key/value pairs the login needs. The agent sees only the keys — values are injected at runtime, never stored or logged.`}
          </p>
          {identity.inputs.map((input, index) => {
            // inputs[0]=username, inputs[1]=password are seeded by
            // makeEmptyIdentity and locked: their keys are fixed labels and cannot
            // be renamed or removed.
            const locked = index < 2;
            return (
              <div key={input.id} className="mb-2 flex items-end gap-2">
                {locked ? (
                  <div className="flex-1">
                    <Input
                      label={input.key}
                      name={`identity-input-${input.key}`}
                      type={
                        input.key === 'password'
                          ? Input.Type.PASSWORD
                          : undefined
                      }
                      password={input.key === 'password'}
                      value={input.value}
                      onChange={e =>
                        updateInput(index, 'value', e.target.value)
                      }
                      placeholder={`Enter ${input.key}`}
                      autoComplete="off"
                      data-1p-ignore
                      data-lpignore="true"
                      data-testid={`identity-input-value-${index}`}
                    />
                  </div>
                ) : (
                  <>
                    <div className="flex-1">
                      <Input
                        label="Key"
                        name={`identity-input-key-${index}`}
                        value={input.key}
                        onChange={e =>
                          updateInput(index, 'key', e.target.value)
                        }
                        placeholder="Key"
                        data-testid={`identity-input-key-${index}`}
                      />
                    </div>
                    <div className="flex-1">
                      <Input
                        label="Value"
                        name={`identity-input-value-${index}`}
                        type={Input.Type.PASSWORD}
                        password
                        value={input.value}
                        onChange={e =>
                          updateInput(index, 'value', e.target.value)
                        }
                        placeholder="Value"
                        autoComplete="off"
                        data-1p-ignore
                        data-lpignore="true"
                        data-testid={`identity-input-value-${index}`}
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        onChange({
                          ...identity,
                          inputs: identity.inputs.filter((_, i) => i !== index),
                        })
                      }
                      aria-label="Remove input"
                    >
                      <TrashIcon className="size-4" />
                    </Button>
                  </>
                )}
              </div>
            );
          })}
          <Button
            variant="ghost"
            size="sm"
            onClick={() =>
              onChange({
                ...identity,
                inputs: [
                  ...identity.inputs,
                  { id: crypto.randomUUID(), key: '', value: '' },
                ],
              })
            }
            data-testid="identity-add-input"
          >
            <PlusIcon className="size-4" />
            Add Input
          </Button>
          {showError && error?.inputs && (
            <p className="text-error mt-1 text-xs" aria-live="polite">
              {error.inputs}
            </p>
          )}
        </div>

        {/* Recorder note (copied verbatim from ComplexLoginForm). */}
        <div className="bg-chariot-background-secondary rounded-lg p-3">
          <p className="text-chariot-text-secondary text-sm">
            <strong>Note:</strong>{' '}
            {`Submitting starts a recorder agent. On success, the recipe is verified by deterministic replay and saved.`}
          </p>
        </div>
      </TabsContent>

      {/* Advanced: MFA summary + launcher (opens the separate optional dialog). */}
      <TabsContent value="advanced" className="flex flex-col gap-4">
        <div
          className="border-chariot-stroke bg-chariot-background-primary flex items-center justify-between gap-3 rounded-md border p-3"
          data-testid="identity-mfa-summary"
        >
          <div>
            <div className="text-chariot-text-primary text-sm font-medium">
              Two-factor / TOTP
            </div>
            <div className="text-chariot-text-secondary text-sm">
              {identity.totpSecret
                ? 'Secret set for this identity'
                : 'Optional — not configured'}
            </div>
          </div>
          <Button
            variant="secondary"
            size="sm"
            onClick={onOpenMfa}
            data-testid="identity-mfa-launcher"
          >
            {identity.totpSecret ? 'Edit MFA' : 'Add MFA'}
          </Button>
        </div>
        {showError && error?.mfa && (
          <p className="text-error mt-1 text-xs" aria-live="polite">
            {error.mfa}
          </p>
        )}
      </TabsContent>
    </Tabs>
  );
}
